import React, { createFactory, memo } from 'react'

import PropTypes from 'prop-types'
import { compose, withState, setPropTypes, defaultProps } from 'recompose'
import { withNamespaces } from 'react-i18next'
import { withStyles } from '@material-ui/core'

import MuiDialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'

import CsgButton from 'components/atoms/Button'
import CsgErrorButton from 'components/atoms/ErrorButton'
import CsgTypography from 'components/atoms/Typography'
import CsgPureHtml from 'components/atoms/PureHtml'

const styles = theme => ({
  borderPaperError: {
    borderColor: theme.palette.error.main,
    borderWidth: 1,
    borderStyle: 'solid'
  }
})

const ModalConfirmation = ({
  t,
  message,
  classes,
  onConfirm,
  onCancel,
  tReady,
  reportNS,
  defaultNS,
  i18nOptions,
  confirmDisabled,
  cancelDisabled,
  error,
  actionButtonText,
  actionButtonCancelText,
  withCancelButton,
  maxWidth,
  bgColor,
  ...other
}) => {
  const Button = error ? CsgErrorButton : CsgButton
  return (
    <>
      <MuiDialog
        classes={{
          paper: error ? classes.borderPaperError : ''
        }}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth={maxWidth}
        aria-labelledby="confirmation-dialog-title"
        {...other}
      >
        <MuiDialogContent>
          <CsgTypography>
            <CsgPureHtml html={message} />
          </CsgTypography>
        </MuiDialogContent>

        <MuiDialogActions>
          {withCancelButton && (
            <CsgButton
              id={`modal-btn-${actionButtonCancelText || t('global_cancel')}`}
              onClick={onCancel}
              variant="outlined"
              disabled={cancelDisabled}
            >
              {actionButtonCancelText || t('global_cancel')}
            </CsgButton>
          )}

          <Button
            id={`modal-btn-${actionButtonText || t('global_confirm')}`}
            onClick={onConfirm}
            color="primary"
            variant="contained"
            bgColor={bgColor}
            disabled={confirmDisabled}
          >
            {actionButtonText || t('global_confirm')}
          </Button>
        </MuiDialogActions>
      </MuiDialog>
    </>
  )
}

const enhance = compose(
  withNamespaces('translation'),
  withStyles(styles),
  setPropTypes({
    message: PropTypes.string.isRequired,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    open: PropTypes.bool,
    confirmDisabled: PropTypes.bool,
    cancelDisabled: PropTypes.bool,
    withCancelButton: PropTypes.bool
  }),
  defaultProps({
    onConfirm: () => {},
    open: false,
    confirmDisabled: false,
    cancelDisabled: false,
    withCancelButton: true
  })
)

export default enhance(ModalConfirmation)

const enhanceFunction = compose(
  withState('modalOpened', 'setModalOpened', false),
  withState('message', 'setMessage', ''),
  withState('error', 'setError', false),
  withState('actionButtonText', 'setActionButtonText', ''),
  withState('actionButtonCancelText', 'setActionButtonCancelText', ''),
  withState('onConfirm', 'setOnConfirm', () => {}),
  withState('onCancel', 'setOnCancel', () => {}),
  withState('withCancelButton', 'setWithCancelButton', true),
  withState('maxWidth', 'setMaxWidth', 'xs')
)

export const withModalConfirmation = Component => {
  return enhanceFunction(
    memo(
      ({
        modalOpened,
        setModalOpened,
        message,
        setMessage,
        onConfirm,
        setOnConfirm,
        onCancel,
        setOnCancel,
        error,
        setError,
        actionButtonText,
        setActionButtonText,
        setActionButtonCancelText,
        actionButtonCancelText,
        setWithCancelButton,
        withCancelButton,
        setMaxWidth,
        maxWidth,
        ...props
      }) => {
        const MyModal = enhance(ModalConfirmation)
        const factory = createFactory(Component)
        const requestConfirmation = ({
          message,
          error = false,
          actionButtonText,
          actionButtonCancelText,
          withCancelButton,
          maxWidth = 'xs'
        }) =>
          new Promise(resolve => {
            const close = response => () => {
              setMessage('')
              setError(false)
              setActionButtonText('')
              setActionButtonCancelText('')

              setModalOpened(false)
              resolve(response)
            }
            setMessage(message)
            setError(error)
            setActionButtonText(actionButtonText)
            setActionButtonCancelText(actionButtonCancelText)
            setWithCancelButton(withCancelButton)
            setMaxWidth(maxWidth)

            setModalOpened(true)
            setOnConfirm(() => close(true))
            setOnCancel(() => close(false))
          })

        return (
          <>
            <MyModal
              open={modalOpened}
              message={message}
              onConfirm={onConfirm}
              onCancel={onCancel}
              actionButtonText={actionButtonText}
              actionButtonCancelText={actionButtonCancelText}
              withCancelButton={withCancelButton}
              maxWidth={maxWidth}
              error={error}
            />
            {factory({ ...props, requestConfirmation })}
          </>
        )
      }
    )
  )
}
